import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Progress,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { projectsAPI, workTypesAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import SideModal from '../../components/Common/SideModal';

interface WorkType {
  id: string;
  name: string;
  unit: string;
}

interface SiteWork {
  id: string;
  targetQuantity: number | null;
  completedQuantity: number;
  progressPercentage: number | null;
  status: string;
  notes: string | null;
  workType: WorkType;
}

interface Site {
  id: string;
  name: string;
  code: string;
  project: { id: string; name: string };
}

const statusColors: Record<string, string> = {
  NOT_STARTED: 'secondary',
  IN_PROGRESS: 'primary',
  COMPLETED: 'success',
};

const statusLabels: Record<string, string> = {
  NOT_STARTED: 'Başlamadı',
  IN_PROGRESS: 'Devam Ediyor',
  COMPLETED: 'Tamamlandı',
};

const SiteWorks: React.FC = () => {
  const { siteId } = useParams();
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [site, setSite] = useState<Site | null>(null);
  const [siteWorks, setSiteWorks] = useState<SiteWork[]>([]);
  const [unassignedWorkTypes, setUnassignedWorkTypes] = useState<WorkType[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSiteWork, setSelectedSiteWork] = useState<SiteWork | null>(null);

  const [formData, setFormData] = useState({
    workTypeId: '',
    targetQuantity: '',
    notes: '',
  });

  const [editFormData, setEditFormData] = useState({
    targetQuantity: '',
    status: '',
    notes: '',
  });

  // Cost modal
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [costData, setCostData] = useState<any>(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costSiteWork, setCostSiteWork] = useState<SiteWork | null>(null);

  useEffect(() => {
    if (siteId) {
      fetchData();
    }
  }, [siteId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [siteRes, worksRes, unassignedRes] = await Promise.all([
        projectsAPI.getSite(siteId!),
        workTypesAPI.getSiteWorks(siteId!),
        workTypesAPI.getUnassignedForSite(siteId!),
      ]);
      setSite(siteRes.data);
      setSiteWorks(worksRes.data);
      setUnassignedWorkTypes(unassignedRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({ workTypeId: '', targetQuantity: '', notes: '' });
    setModalOpen(true);
  };

  const openEditModal = (sw: SiteWork) => {
    setSelectedSiteWork(sw);
    setEditFormData({
      targetQuantity: sw.targetQuantity?.toString() || '',
      status: sw.status,
      notes: sw.notes || '',
    });
    setEditModalOpen(true);
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.workTypeId) {
      toast.error('İş türü seçin');
      return;
    }

    try {
      setSubmitting(true);
      await workTypesAPI.assignToSite(siteId!, {
        workTypeId: formData.workTypeId,
        targetQuantity: formData.targetQuantity ? Number(formData.targetQuantity) : undefined,
        notes: formData.notes || undefined,
      });
      toast.success('İş atandı');
      setModalOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSiteWork) return;

    try {
      setSubmitting(true);
      await workTypesAPI.updateSiteWork(selectedSiteWork.id, {
        targetQuantity: editFormData.targetQuantity ? Number(editFormData.targetQuantity) : undefined,
        status: editFormData.status,
        notes: editFormData.notes || undefined,
      });
      toast.success('İş güncellendi');
      setEditModalOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm('Bu işi şantiyeden kaldırmak istediğinize emin misiniz?')) {
      return;
    }

    try {
      await workTypesAPI.removeSiteWork(id);
      toast.success('İş kaldırıldı');
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  const openCostModal = async (sw: SiteWork) => {
    setCostSiteWork(sw);
    setCostData(null);
    setCostModalOpen(true);
    try {
      setCostLoading(true);
      const response = await workTypesAPI.getSiteWorkCost(sw.id);
      setCostData(response.data);
    } catch (error: any) {
      toast.error('Maliyet hesaplanamadı');
      console.error('Cost fetch error:', error);
    } finally {
      setCostLoading(false);
    }
  };

  const getProgressColor = (percentage: number | null) => {
    if (percentage === null) return 'secondary';
    if (percentage >= 100) return 'success';
    if (percentage >= 50) return 'primary';
    if (percentage >= 25) return 'warning';
    return 'danger';
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-sm-0">Şantiye İşleri</h4>
              {site && (
                <p className="text-muted mb-0">
                  {site.project.name} / {site.name} ({site.code})
                </p>
              )}
            </div>
            <div className="page-title-right d-flex gap-2">
              {site && (
                <Link to={`/projects/${site.project.id}/sites`} className="btn btn-outline-secondary">
                  <i className="ri-arrow-left-line me-1"></i>
                  Şantiyelere Dön
                </Link>
              )}
              {isAdmin && unassignedWorkTypes.length > 0 && (
                <Button color="primary" onClick={openAddModal}>
                  <i className="ri-add-line me-1"></i>
                  İş Ata
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Atanmış İşler</h5>
              <Badge color="soft-primary" className="fs-6">
                {siteWorks.length} iş
              </Badge>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Yükleniyor...</p>
                </div>
              ) : siteWorks.length === 0 ? (
                <div className="text-center py-5">
                  <i className="ri-tools-line display-4 text-muted"></i>
                  <p className="mt-3 text-muted">Bu şantiyeye henüz iş atanmamış</p>
                  {isAdmin && unassignedWorkTypes.length > 0 && (
                    <Button color="primary" size="sm" onClick={openAddModal}>
                      İlk İşi Ata
                    </Button>
                  )}
                </div>
              ) : isMobile ? (
                // Mobile: Card View
                <div className="admin-card-list">
                  {siteWorks.map((sw) => (
                    <div key={sw.id} className="admin-card-item">
                      {/* Card Header */}
                      <div className="admin-card-header">
                        <div>
                          <h6 className="admin-card-title">{sw.workType.name}</h6>
                          {sw.notes && (
                            <small className="text-muted">{sw.notes}</small>
                          )}
                        </div>
                        <Badge color={statusColors[sw.status]}>
                          {statusLabels[sw.status]}
                        </Badge>
                      </div>

                      {/* Card Body */}
                      <div className="admin-card-body">
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-ruler-line"></i>
                            Birim
                          </span>
                          <span className="admin-info-value">
                            <Badge color="soft-info">{sw.workType.unit}</Badge>
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-focus-line"></i>
                            Hedef
                          </span>
                          <span className="admin-info-value">
                            {sw.targetQuantity ? (
                              <span>{sw.targetQuantity.toLocaleString('tr-TR')}</span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-checkbox-circle-line"></i>
                            Yapılan
                          </span>
                          <span className="admin-info-value">
                            <strong>{sw.completedQuantity.toLocaleString('tr-TR')}</strong>
                          </span>
                        </div>
                        {sw.targetQuantity && (
                          <div className="mt-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <small className="text-muted">İlerleme</small>
                              <small className="text-muted">%{sw.progressPercentage || 0}</small>
                            </div>
                            <Progress
                              value={sw.progressPercentage || 0}
                              color={getProgressColor(sw.progressPercentage)}
                              style={{ height: 8 }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Card Footer */}
                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-warning"
                          onClick={() => openCostModal(sw)}
                        >
                          <i className="ri-money-dollar-circle-line"></i>
                          Maliyet
                        </button>
                        {isAdmin && (
                          <>
                            <button
                              type="button"
                              className="admin-action-btn admin-btn-primary"
                              onClick={() => openEditModal(sw)}
                            >
                              <i className="ri-pencil-line"></i>
                              Düzenle
                            </button>
                            <button
                              type="button"
                              className="admin-action-btn admin-btn-danger"
                              onClick={() => handleRemove(sw.id)}
                            >
                              <i className="ri-delete-bin-line"></i>
                              Sil
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop: Table View
                <div className="table-responsive">
                  <Table className="table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>İş Adı</th>
                        <th>Birim</th>
                        <th>Hedef</th>
                        <th>Yapılan</th>
                        <th style={{ width: 200 }}>İlerleme</th>
                        <th>Durum</th>
                        <th style={{ width: 100 }}>Maliyet</th>
                        {isAdmin && <th style={{ width: 120 }}>İşlemler</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {siteWorks.map((sw) => (
                        <tr key={sw.id}>
                          <td>
                            <strong>{sw.workType.name}</strong>
                            {sw.notes && (
                              <small className="d-block text-muted">{sw.notes}</small>
                            )}
                          </td>
                          <td>
                            <Badge color="soft-info">{sw.workType.unit}</Badge>
                          </td>
                          <td>
                            {sw.targetQuantity ? (
                              <span>{sw.targetQuantity.toLocaleString('tr-TR')}</span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <strong>{sw.completedQuantity.toLocaleString('tr-TR')}</strong>
                          </td>
                          <td>
                            {sw.targetQuantity ? (
                              <div>
                                <Progress
                                  value={sw.progressPercentage || 0}
                                  color={getProgressColor(sw.progressPercentage)}
                                  className="mb-1"
                                  style={{ height: 8 }}
                                />
                                <small className="text-muted">
                                  %{sw.progressPercentage || 0}
                                </small>
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <Badge color={statusColors[sw.status]}>
                              {statusLabels[sw.status]}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              color="soft-warning"
                              size="sm"
                              onClick={() => openCostModal(sw)}
                              title="Maliyet Detayı"
                            >
                              <i className="ri-money-dollar-circle-line"></i>
                            </Button>
                          </td>
                          {isAdmin && (
                            <td>
                              <Button
                                color="soft-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => openEditModal(sw)}
                              >
                                <i className="ri-pencil-line"></i>
                              </Button>
                              <Button
                                color="soft-danger"
                                size="sm"
                                onClick={() => handleRemove(sw.id)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add Work Modal */}
      <SideModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title="Şantiyeye İş Ata"
        footer={
          <>
            <Button color="light" onClick={() => setModalOpen(false)} disabled={submitting}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="assign-work-form" disabled={submitting}>
              {submitting ? 'Atanıyor...' : 'Ata'}
            </Button>
          </>
        }
      >
        <Form id="assign-work-form" onSubmit={handleAssign}>
          <FormGroup>
            <Label>İş Türü *</Label>
            <Input
              type="select"
              value={formData.workTypeId}
              onChange={(e) => setFormData({ ...formData, workTypeId: e.target.value })}
              required
            >
              <option value="">Seçin...</option>
              {unassignedWorkTypes.map((wt) => (
                <option key={wt.id} value={wt.id}>
                  {wt.name} ({wt.unit})
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Hedef Miktar</Label>
            <Input
              type="number"
              value={formData.targetQuantity}
              onChange={(e) => setFormData({ ...formData, targetQuantity: e.target.value })}
              placeholder="Örn: 5000"
              min="0"
              step="0.01"
            />
            <small className="text-muted">Hedef belirlerseniz ilerleme takibi yapılır</small>
          </FormGroup>
          <FormGroup>
            <Label>Not</Label>
            <Input
              type="textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Opsiyonel not..."
              rows={2}
            />
          </FormGroup>
        </Form>
      </SideModal>

      {/* Edit Work Modal */}
      <SideModal
        isOpen={editModalOpen}
        toggle={() => setEditModalOpen(false)}
        title={`İş Düzenle: ${selectedSiteWork?.workType.name || ''}`}
        footer={
          <>
            <Button color="light" onClick={() => setEditModalOpen(false)} disabled={submitting}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="edit-work-form" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <Form id="edit-work-form" onSubmit={handleUpdate}>
          <FormGroup>
            <Label>Hedef Miktar ({selectedSiteWork?.workType.unit})</Label>
            <Input
              type="number"
              value={editFormData.targetQuantity}
              onChange={(e) => setEditFormData({ ...editFormData, targetQuantity: e.target.value })}
              placeholder="Örn: 5000"
              min="0"
              step="0.01"
            />
          </FormGroup>
          <FormGroup>
            <Label>Durum</Label>
            <Input
              type="select"
              value={editFormData.status}
              onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
            >
              <option value="NOT_STARTED">Başlamadı</option>
              <option value="IN_PROGRESS">Devam Ediyor</option>
              <option value="COMPLETED">Tamamlandı</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Not</Label>
            <Input
              type="textarea"
              value={editFormData.notes}
              onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              placeholder="Opsiyonel not..."
              rows={2}
            />
          </FormGroup>
        </Form>
      </SideModal>
      {/* Cost Detail Modal */}
      <SideModal
        isOpen={costModalOpen}
        toggle={() => setCostModalOpen(false)}
        title={`Maliyet: ${costSiteWork?.workType.name || ''}`}
        size="lg"
        hideBackdrop
      >
        <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {costLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Maliyet hesaplanıyor...</p>
          </div>
        ) : !costData ? (
          <div className="text-center py-5 text-muted">
            <i className="ri-error-warning-line display-4"></i>
            <p className="mt-2">Maliyet bilgisi yüklenemedi</p>
          </div>
        ) : costData.materials.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="ri-box-3-line display-4"></i>
            <p className="mt-2">
              {costData.totalCost === 0
                ? 'Bu iş türüne malzeme eşleştirmesi yapılmamış veya şantiyeye eşleşen irsaliye yok'
                : 'Maliyet bilgisi bulunamadı'}
            </p>
            <small>İş türü detayından malzeme eşleştirmesi yapabilirsiniz</small>
          </div>
        ) : (
          <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
            {/* Özet */}
            <div className="row g-2 mb-4">
              <div className="col-6 col-md-3">
                <div className="bg-light rounded-3 p-3 text-center">
                  <div className="text-muted small mb-1">Yapılan</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    {costData.completedQuantity.toLocaleString('tr-TR')} {costData.unit}
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="bg-light rounded-3 p-3 text-center">
                  <div className="text-muted small mb-1">Malzeme</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#405189' }}>
                    {costData.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="bg-light rounded-3 p-3 text-center">
                  <div className="text-muted small mb-1">KDV</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#f06548' }}>
                    {costData.totalVat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="bg-light rounded-3 p-3 text-center">
                  <div className="text-muted small mb-1">Birim Maliyet</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0ab39c' }}>
                    {costData.unitCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺/{costData.unit}
                  </div>
                </div>
              </div>
            </div>

            {/* Malzeme Detay Tablosu */}
            <div className="text-muted small mb-2" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-file-list-3-line me-1"></i>
              Malzeme Detayları
            </div>
            <div className="table-responsive">
              <Table className="table-sm table-striped align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>İrsaliye</th>
                    <th>Malzeme</th>
                    <th className="text-end">Miktar</th>
                    <th className="text-end">Fiyat</th>
                    <th className="text-end">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {costData.materials.map((m: any, idx: number) => (
                    <tr key={idx}>
                      <td>
                        <small className="text-muted">{m.waybillNumber}</small>
                      </td>
                      <td>
                        <span style={{ fontWeight: 500 }}>{m.itemName}</span>
                        <small className="text-muted d-block">{m.itemCode}</small>
                      </td>
                      <td className="text-end">
                        {m.amount.toLocaleString('tr-TR')} {m.unitName || ''}
                      </td>
                      <td className="text-end">
                        {m.price > 0 ? `${m.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}
                      </td>
                      <td className="text-end" style={{ fontWeight: 500 }}>
                        {m.total > 0 ? `${m.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="table-warning">
                    <td colSpan={4} className="text-end" style={{ fontWeight: 600 }}>Toplam (KDV Hariç)</td>
                    <td className="text-end" style={{ fontWeight: 600 }}>
                      {costData.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="text-end text-muted">KDV</td>
                    <td className="text-end text-muted">
                      {costData.totalVat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </td>
                  </tr>
                  <tr className="table-success">
                    <td colSpan={4} className="text-end" style={{ fontWeight: 600 }}>Genel Toplam</td>
                    <td className="text-end" style={{ fontWeight: 600 }}>
                      {costData.grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </div>
        )}
        </div>
      </SideModal>
    </>
  );
};

export default SiteWorks;
